import {
  MorphoUserReserveResponse,
  ListaTermLoan,
} from '../../../types/lenderTypes'

/** loanId of the dynamic (flexible) broker loan — matches the LISTA_BROKER_REPAY dynamic sentinel
 *  (type(uint128).max), which is the on-chain repay target for the flex position. */
const LISTA_DYNAMIC_LOAN_ID = (2n ** 128n - 1n).toString()
import { createBaseTypeUserState } from '../utils'
import { parseRawAmount } from '../../../utils/parsing'
import {
  BalanceInfo,
  decodePackedListaUserDataset,
  decodePackedMorphoUserDataset,
} from './decoder'
import { isLista } from '@1delta/lender-registry'
import { createMarketUid } from '../../../utils/marketUid'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  LenderYieldComplete,
  UserData,
} from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import {
  getCachedListaBrokerUserData,
  ListaBrokerUserData,
} from '../../public-data/lista/listaBroker'

const CHUNK_SIZE = 100

function createLenderKey(lender: string, marketId: string) {
  if (marketId.startsWith(lender)) return marketId
  return `${lender}_${marketId.replace('0x', '').toUpperCase()}`
}

const balanceData = {
  borrowDiscountedCollateral: 0,
  borrowDiscountedCollateralAllActive: 0,
  collateral: 0,
  deposits: 0,
  debt: 0,
  adjustedDebt: 0,
  nav: 0,
  deposits24h: 0,
  debt24h: 0,
  nav24h: 0,
}

// aggregated apr data
const aprData = {
  apr: 0,
  borrowApr: 0,
  depositApr: 0,
  rewards: 0,
  rewardApr: 0,
  rewardDepositApr: 0,
  rewardBorrowApr: 0,
  intrinsicApr: 0,
  intrinsicDepositApr: 0,
  intrinsicBorrowApr: 0,
}
const userDataEmpty = (
  account: string,
  chainId: string,
  notWhitelisted = false,
) => {
  return {
    account,
    chainId,
    data: [
      {
        health: null,
        accouintId: '0',
        balanceData,
        aprData,
        positions: [],
        userConfig: { selectedMode: '0', id: account, notWhitelisted },
      },
    ],
  }
}
export const getMorphoTypeUserDataConverterWithlens = (
  lender: string,
  chainId: string,
  account: string,
  markets: string[],
  metaMap?: LenderToLenderCrossPoolMeta,
) => {
  return !isLista(lender)
    ? getMorphoUserDataConverterWithlens(
        lender,
        chainId,
        account,
        markets,
        metaMap,
      )
    : getListaUserDataConverterWithlens(
        lender,
        chainId,
        account,
        markets,
        metaMap,
      )
}

export const getMorphoUserDataConverterWithlens = (
  lender: string,
  chainId: string,
  account: string,
  markets: string[],
  metaMap?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lenderKey: string]: UserData } | undefined, number] => {
  const expectedNumberOfCalls = Math.ceil(markets.length / CHUNK_SIZE)
  return [
    (data: any[]) => {
      // chunksizes depend on markets
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }

      // we map the index from the return data
      // to the real chunk
      // it is
      //  (chunk number) * (chunksize = 100) + index
      function mapMarketToChunk(indexInChunk: number, chunkIndex: number) {
        return chunkIndex * CHUNK_SIZE + indexInChunk
      }

      let marketsHandled: string[] = []

      let datas: { [market: string]: UserData } = {}

      // we loop over the return dat
      for (let i = 0; i < data.length; i++) {
        // get balance data array for chunk
        const balanceDatas = decodePackedMorphoUserDataset(data[i])

        // itereate of balanceDatas received in chunk
        balanceDatas.forEach((balanceData) => {
          // this will get the market by index
          const marketId = markets[mapMarketToChunk(balanceData.index, i)]

          marketsHandled.push(marketId)

          const lenderKey = createLenderKey(lender, marketId)

          const { dataForMarket, addedDebt, addedDeposits } =
            createMorphoEntryFromMarketWithLens(
              balanceData,
              metaMap?.[lenderKey],
              chainId,
              lenderKey,
            )

          const payload = {
            chainId,
            account,
            lendingPositions: dataForMarket,
            rewards: [],
            userEMode: 0,
          }

          const userData = createBaseTypeUserState(
            payload,
            metaMap?.[lenderKey]!,
            addedDeposits,
            addedDebt,
            lenderKey,
          )

          datas[lenderKey] = userData
        })
      }

      for (const marketId of markets) {
        if (marketsHandled.includes(marketId)) continue

        const userData = userDataEmpty(account, chainId)
        // @ts-ignore
        datas[marketId] = userData
      }
      return datas
    },
    expectedNumberOfCalls,
  ]
}

export const getListaUserDataConverterWithlens = (
  lender: string,
  chainId: string,
  account: string,
  markets: string[],
  metaMap?: LenderToLenderCrossPoolMeta,
): [
  (data: any[]) => { [market: string]: MorphoUserReserveResponse } | undefined,
  number,
] => {
  const expectedNumberOfCalls = Math.ceil(markets.length / CHUNK_SIZE)
  return [
    (data: any[]) => {
      // chunksizes depend on markets
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }

      // we map the index from the return data
      // to the real chunk
      // it is
      //  (chunk number) * (chunksize = 100) + index
      function mapMarketToChunk(indexInChunk: number, chunkIndex: number) {
        return chunkIndex * CHUNK_SIZE + indexInChunk
      }

      let marketsHandled: string[] = []

      let datas: { [market: string]: MorphoUserReserveResponse } = {}
      let whitelistedFlags: { [market: string]: boolean } = {}

      // we loop over the return dat
      for (let i = 0; i < data.length; i++) {
        // number of markets in THIS chunk (last chunk may be smaller)
        const chunkStart = i * CHUNK_SIZE
        const chunkMarketCount = Math.min(
          CHUNK_SIZE,
          markets.length - chunkStart,
        )

        // get balance data array for chunk
        const { balances, whitelistFlags } = decodePackedListaUserDataset(
          data[i],
          chunkMarketCount,
        )
        whitelistFlags.forEach((flag, index) => {
          whitelistedFlags[markets[mapMarketToChunk(index, i)]] = flag
        })
        // itereate of balanceDatas received in chunk
        balances.forEach((balanceData) => {
          // this will get the market by index
          const marketId = markets[mapMarketToChunk(balanceData.index, i)]

          marketsHandled.push(marketId)

          const lenderKey = createLenderKey(lender, marketId)
          // Lista fixed-term broker breakdown (warm via fetchListaBrokerUserData in the build).
          // Brokered markets only; undefined otherwise.
          const brokerData = getCachedListaBrokerUserData(
            chainId,
            account,
            marketId,
          )
          const { dataForMarket, addedDebt, addedDeposits } =
            createMorphoEntryFromMarketWithLens(
              balanceData,
              metaMap?.[lenderKey],
              chainId,
              lenderKey,
              brokerData,
            )

          const payload = {
            chainId,
            account,
            lendingPositions: dataForMarket,
            rewards: [],
            userEMode: 0,
            notWhitelisted: !whitelistedFlags[marketId],
          }

          const userData = createBaseTypeUserState(
            payload,
            metaMap?.[lenderKey]!,
            addedDeposits,
            addedDebt,
            lenderKey,
          )

          datas[lenderKey] = {
            ...payload,
            ...userData,
            id: marketId,
          }
        })
      }

      for (const marketId of markets) {
        if (marketsHandled.includes(marketId)) continue
        const userData = userDataEmpty(
          account,
          chainId,
          !whitelistedFlags[marketId],
        )
        // @ts-ignore
        datas[marketId] = userData
      }
      return datas
    },
    expectedNumberOfCalls,
  ]
}

export function createMorphoEntryFromMarketWithLens(
  balanceInfo: BalanceInfo,
  metaMap: LenderCrossPoolMeta | undefined,
  chainId: string,
  lenderKey: string,
  brokerData?: ListaBrokerUserData,
) {
  const morphoPair = metaMap
  const _assets: [string, LenderYieldComplete][] = Object.entries(
    morphoPair ?? {},
  ) as any

  // Find loan asset (borrowingEnabled: true) and collateral asset (borrowingEnabled: false)
  // Morpho markets can have multiple assets in metadata, need to find the correct ones by flag
  const loanEntry = _assets.find(([, meta]) => meta?.flags?.borrowingEnabled)
  const collateralEntry = _assets.find(
    ([, meta]) => !meta?.flags?.borrowingEnabled,
  )

  if (!loanEntry || !collateralEntry) {
    // Fallback to old behavior if we can't find distinct loan/collateral
    return {
      dataForMarket: {},
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const [loanKey, loanAssetMeta] = loanEntry
  const [collateralKey, collateralAssetMeta] = collateralEntry
  const loanAddress = loanAssetMeta?.asset?.address?.toLowerCase()!
  const loanAsset = loanAssetMeta?.asset!

  // prices
  const priceLoan = getDisplayPrice(loanAssetMeta)
  const oraclePriceLoan = getOraclePrice(loanAssetMeta)
  const priceHistLoan = loanAssetMeta?.price?.priceUsd24h ?? priceLoan
  // balances
  const deposits = balanceInfo.supplyAssets
  const borrow = balanceInfo.borrowAssets
  const collateral = balanceInfo.collateral
  // return early if there are zeros
  if (deposits === 0n && borrow === 0n && collateral === 0n)
    return {
      dataForMarket: {},
      addedDeposits: 0,
      addedDebt: 0,
    }

  const collateralAddress = collateralAssetMeta?.asset?.address?.toLowerCase()!

  const collateralAsset = collateralAssetMeta?.asset

  // loan asset deposits
  const loanDepositNumber = parseRawAmount(
    deposits.toString(),
    loanAsset?.decimals ?? 18,
  )

  // collateral amount
  const decimals = collateralAsset?.decimals ?? 18
  const collateralDec = parseRawAmount(collateral.toString(), decimals)

  // borrow asset balance
  const borrowDec = parseRawAmount(borrow.toString(), loanAsset?.decimals ?? 18)

  const depositShares = balanceInfo.supplyShares.toString()
  const debtShares = balanceInfo.borrowShares.toString()

  const dataForLoanAsset = {
    marketUid: createMarketUid(chainId, lenderKey, loanAddress),
    underlying: loanAddress,
    deposits: loanDepositNumber,
    debtStable: '0',
    debt: borrowDec,
    depositsUSD: Number(loanDepositNumber) * priceLoan,
    debtStableUSD: 0,
    debtUSD: Number(borrowDec) * priceLoan,
    depositsUSDOracle: Number(loanDepositNumber) * oraclePriceLoan,
    debtStableUSDOracle: 0,
    debtUSDOracle: Number(borrowDec) * oraclePriceLoan,
    stableBorrowRate: '0',
    collateralEnabled: false,
    claimableRewards: 0,
    depositShares,
    debtShares,
  }

  // prices
  const price = getDisplayPrice(collateralAssetMeta)
  const oraclePriceCollateral = getOraclePrice(collateralAssetMeta)
  const priceHist = collateralAssetMeta?.price.priceUsd24h ?? price

  const dataForCollateralAsset = {
    marketUid: createMarketUid(chainId, lenderKey, collateralAddress),
    underlying: collateralAddress,
    deposits: collateralDec,
    debtStable: '0',
    debt: '0',
    depositsUSD: Number(collateralDec) * price,
    debtStableUSD: 0,
    debtUSD: 0,
    depositsUSDOracle: Number(collateralDec) * oraclePriceCollateral,
    debtStableUSDOracle: 0,
    debtUSDOracle: 0,
    stableBorrowRate: '0',
    collateralEnabled: true,
    claimableRewards: 0,
  }

  const loanMarketUid = dataForLoanAsset.marketUid
  const loanDecimals = loanAsset?.decimals ?? 18

  // Lista fixed-term broker — Aave-v3 split on the aggregate loan position: variable `debt` = the
  // dynamic (flexible) loan, fixed `debtStable` = sum of the fixed-term loans. Each loan is also
  // emitted as its own extra positions[] entry under the same accountId, keyed
  // `${loanMarketUid}#${loanId}` (internal key only — not a metadata key, so the shared aggregator
  // skips it → no double-count; marketUid stays canonical for the public join). Fixed loans carry a
  // `term` with the term data; the dynamic loan carries a `term` flagged isDynamic and uses the
  // dynamic loanId sentinel (= the LISTA_BROKER_REPAY dynamic repay target). The aggregate (no
  // `term`) is the rollup used for health; collateral stays a single shared (untagged) position.
  const termPositions: Record<string, any> = {}
  let aggregateDebtToken = borrowDec
  if (brokerData) {
    const mkTermPosition = (
      key: string,
      debtToken: string,
      term: ListaTermLoan,
    ) => {
      termPositions[key] = {
        marketUid: loanMarketUid,
        underlying: loanAddress,
        loanId: term.loanId,
        deposits: '0',
        // fixed loans sit in the stable slot, the dynamic loan in the variable slot
        debt: term.isDynamic ? debtToken : '0',
        debtStable: term.isDynamic ? '0' : debtToken,
        depositsUSD: 0,
        debtUSD: term.isDynamic ? Number(debtToken) * priceLoan : 0,
        debtStableUSD: term.isDynamic ? 0 : Number(debtToken) * priceLoan,
        depositsUSDOracle: 0,
        debtUSDOracle: term.isDynamic ? Number(debtToken) * oraclePriceLoan : 0,
        debtStableUSDOracle: term.isDynamic ? 0 : Number(debtToken) * oraclePriceLoan,
        collateralEnabled: false,
        claimableRewards: 0,
        term,
      }
    }

    let sumFixedRaw = 0n
    // Σ(tranche debt × loan apr), to derive the user's debt-weighted fixed borrow rate. The
    // market-level `stableBorrowRate` is only the *cheapest* term on the menu (and is 0 when the
    // term menu isn't cached), so weighting the user's actual locked loans is what makes the
    // brokered fixed APR show up correctly in the blended borrow APR.
    let weightedAprNumerator = 0
    for (const loan of brokerData.fixedLoans) {
      const trancheRaw = BigInt(loan.outstanding) + BigInt(loan.accruedInterest)
      sumFixedRaw += trancheRaw
      const trancheToken = parseRawAmount(trancheRaw.toString(), loanDecimals)
      weightedAprNumerator += Number(trancheToken) * loan.apr
      mkTermPosition(`${loanMarketUid}#${loan.posId}`, trancheToken, {
        loanId: loan.posId,
        termId: loan.termId,
        debt: trancheToken,
        apr: loan.apr,
        maturity: loan.end,
        termDays: loan.termDays,
        accruedInterest: parseRawAmount(loan.accruedInterest, loanDecimals),
        earlyRepayPenalty: parseRawAmount(loan.earlyRepayPenalty, loanDecimals),
        isMatured: loan.isMatured,
      })
    }

    // dynamic (flexible) loan = total broker debt − sum of fixed loans. Exists when the user
    // borrowed flex directly or a matured fixed loan was refinanced into the dynamic position.
    const totalRaw = BigInt(brokerData.totalDebt)
    const dynRaw = totalRaw > sumFixedRaw ? totalRaw - sumFixedRaw : 0n
    const dynToken = parseRawAmount(dynRaw.toString(), loanDecimals)
    const fixedToken = parseRawAmount(sumFixedRaw.toString(), loanDecimals)

    // aggregate split: variable = dynamic, stable = fixed
    dataForLoanAsset.debt = dynToken
    dataForLoanAsset.debtUSD = Number(dynToken) * priceLoan
    dataForLoanAsset.debtUSDOracle = Number(dynToken) * oraclePriceLoan
    dataForLoanAsset.debtStable = fixedToken
    dataForLoanAsset.debtStableUSD = Number(fixedToken) * priceLoan
    dataForLoanAsset.debtStableUSDOracle = Number(fixedToken) * oraclePriceLoan
    // The user's debt-weighted fixed borrow rate (percent) across their fixed loans. Surfaced on
    // the aggregate position and consumed by the blended-APR calc in createGeneralUserState.
    const fixedTokenNum = Number(fixedToken)
    dataForLoanAsset.stableBorrowRate =
      fixedTokenNum > 0 ? (weightedAprNumerator / fixedTokenNum).toString() : '0'
    aggregateDebtToken = parseRawAmount(totalRaw.toString(), loanDecimals)

    if (dynRaw > 0n) {
      mkTermPosition(`${loanMarketUid}#dynamic`, dynToken, {
        loanId: LISTA_DYNAMIC_LOAN_ID,
        isDynamic: true,
        debt: dynToken,
        // the flexible loan accrues at the underlying Moolah market's variable borrow rate
        // (PERCENT). Expired fixed positions refinance into this dynamic loan, so without
        // this the migrated debt would (incorrectly) report a 0% borrow rate.
        apr: loanAssetMeta?.variableBorrowRate ?? 0,
      })
    }
  }

  return {
    dataForMarket: {
      [loanKey]: dataForLoanAsset,
      [collateralKey]: dataForCollateralAsset,
      ...termPositions,
    },
    addedDeposits:
      Number(loanDepositNumber) * priceHistLoan +
      Number(collateralDec) * priceHist,
    addedDebt: Number(aggregateDebtToken) * priceHistLoan,
  }
}
